import jsPDF from 'jspdf';
import analyticsService from './analyticsService';

// Simple function to strip markdown and get clean text
const stripMarkdownFromText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    // Remove bold markers (**text** or __text__)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    // Remove italic markers (*text* or _text_) - simplified approach
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // Remove inline code markers (`code`)
    .replace(/`([^`]+)`/g, '$1')
    // Remove link markers [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove header markers (# ## ### etc.)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove blockquote markers (> text)
    .replace(/^>\s*/gm, '')
    // Remove bullet point markers (- * +)
    .replace(/^\s*[-*+]\s+/gm, '• ')
    // Remove numbered list markers (1. 2. etc.)
    .replace(/^\s*\d+\.\s+/gm, '')
    // Clean up any remaining multiple asterisks or underscores
    .replace(/\*+/g, '')
    .replace(/_+/g, '')
    // Clean up extra whitespace and normalize line breaks
    .replace(/\s+/g, ' ')
    .replace(/\n\s*\n/g, '\n')
    .trim();
};



// Ultra-simple function to add clean text without any markdown formatting
const addSimpleCleanText = (doc, text, startX, startY, maxWidth, options = {}) => {
  const { 
    normalFontSize = 10,
    lineHeight = 5
  } = options;

  // Strip all markdown formatting from the text
  const cleanText = stripMarkdownFromText(text);
  
  let yPosition = startY;
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  // Set normal font
  doc.setFontSize(normalFontSize);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  // Split text into lines that fit within the page width
  const splitText = doc.splitTextToSize(cleanText, maxWidth);
  
  // Add each line
  for (const line of splitText) {
    // Check if we need a new page
    if (yPosition + lineHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = 30;
    }
    
    doc.text(line, startX, yPosition);
    yPosition += lineHeight;
  }
  
  return yPosition + 5; // Add some spacing after the text block
};

// Main function to download conversation as PDF with enhanced markdown support
export const downloadConversationAsPdf = (messages) => {
  if (!messages || messages.length === 0) {
    console.warn('No messages to export');
    return;
  }

  // Track download action
  analyticsService.trackEngagement('file_download', { 
    file_type: 'conversation_pdf',
    message_count: messages.length 
  });

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition = 30;

  // Add title
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('Conversation with Talyora about Qaoud', margin, yPosition);
  yPosition += 5;

  // Add date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Downloaded on: ${new Date().toLocaleString()}`, margin, yPosition);
  yPosition += 25;

  // Add messages with enhanced markdown support using jsPDF's reliable text handling
  messages.forEach((msg, index) => {
    const timestamp = new Date(msg.timestamp).toLocaleString();
    const sender = msg.isUser ? 'You' : 'Talyora';
    
    // Check if we need a new page for the message header
    if (yPosition + 40 > pageHeight - margin) {
      doc.addPage();
      yPosition = 30;
    }

    // Add sender and timestamp with better formatting
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(`${sender} - ${timestamp}`, margin, yPosition);
    yPosition += 5;

    // Reset font for message content
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);

    // Add message text with all markdown stripped for clean, readable output
    yPosition = addSimpleCleanText(
      doc, 
      msg.text, 
      margin, 
      yPosition, 
      maxWidth,
      {
        normalFontSize: 10,
        lineHeight: 4.5
      }
    );

    // Add spacing after message
    yPosition += 15;
  });

  // Save the PDF with a descriptive filename
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
  const filename = `Talyora-Conversation-${timestamp}.pdf`;
  doc.save(filename);
  
  console.log(`✅ Successfully exported conversation as ${filename} with clean text (markdown stripped)`);
  return filename;
};

// Additional utility function for future use with enhanced markdown support
export const downloadTextAsPdf = (text, title = 'Document', filename = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition = 30;

  // Add title
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(title, margin, yPosition);
  yPosition += 20;

  // Add date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, yPosition);
  yPosition += 20;

  // Add content with all markdown stripped for clean, readable output
  addSimpleCleanText(doc, text, margin, yPosition, maxWidth, {
    normalFontSize: 10,
    lineHeight: 5
  });

  // Save the PDF
  const pdfFilename = filename || `${title.replace(/\s+/g, '-')}-${new Date().toISOString().replace(/[:.]/g, '-').split('T')[0]}.pdf`;
  doc.save(pdfFilename);
  
  console.log(`✅ Successfully exported text as ${pdfFilename} with clean text (markdown stripped)`);
  return pdfFilename;
};

// Default export object
const pdfService = {
  downloadConversationAsPdf,
  downloadTextAsPdf
};

export default pdfService;